import type { FC } from 'react';
import S, { cm, kw, fn, pr, st, nm } from '../features/shared/SyntaxSpan';

const CompositeSystemMockup: FC = () => (
  <div className="rounded-lg border border-gray-200/80 overflow-hidden shadow-lg" style={{ background: '#1e1e2e' }}>
    {/* Top bar */}
    <div className="flex items-center justify-between px-4 py-2 border-b border-white/[0.06]" style={{ background: '#0c1520' }}>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1.5">
          <span className="w-[7px] h-[7px] rounded-full bg-red-500/40" />
          <span className="w-[7px] h-[7px] rounded-full bg-yellow-500/40" />
          <span className="w-[7px] h-[7px] rounded-full bg-green-500/40" />
        </div>
        <span className="text-[10px] text-gray-500 font-mono">Quanthop &mdash; Research Pipeline</span>
      </div>
      <div className="hidden sm:flex items-center gap-3 text-[9px] text-gray-600 font-mono select-none">
        <span>ema_cross v3</span>
        <span className="text-gray-700">|</span>
        <span>BTCUSDT &middot; 1D</span>
        <span className="text-gray-700">|</span>
        <div className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400/70" />
          <span>Pipeline Complete</span>
        </div>
      </div>
    </div>

    {/* 3-panel layout */}
    <div className="flex flex-col md:flex-row font-mono text-[11px] leading-[1.6]">

      {/* Left: Code Editor */}
      <div className="flex-1 min-w-0 border-b md:border-b-0 md:border-r border-white/[0.06]">
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-white/[0.06]" style={{ background: '#181825' }}>
          <div className="flex items-center gap-2">
            <svg className="w-3 h-3 text-blue-400/70" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            <span className="text-[10px] text-gray-300">strategy.qsl</span>
          </div>
          <span className="text-[9px] text-gray-600 uppercase tracking-wider">Definition</span>
        </div>
        <div className="p-3 text-gray-300/90 whitespace-pre" style={{ background: '#1e1e2e' }}>
          <div><S c={cm}>{'// EMA Cross Strategy'}</S></div>
          <div><S c={kw}>function</S> <S c={fn}>define</S>(<S c={pr}>ctx</S>) {'{'}</div>
          <div>&nbsp;&nbsp;<S c={pr}>ctx</S>.<S c={fn}>param</S>(<S c={st}>&apos;fast&apos;</S>, {'{'}</div>
          <div>&nbsp;&nbsp;&nbsp;&nbsp;<S c={pr}>type</S>: <S c={st}>&apos;int&apos;</S>, <S c={pr}>default</S>: <S c={nm}>9</S>,</div>
          <div>&nbsp;&nbsp;&nbsp;&nbsp;<S c={pr}>min</S>: <S c={nm}>5</S>, <S c={pr}>max</S>: <S c={nm}>50</S>,</div>
          <div>&nbsp;&nbsp;&nbsp;&nbsp;<S c={pr}>optimize</S>: <S c={kw}>true</S>,</div>
          <div>&nbsp;&nbsp;{'}'});</div>
          <div>&nbsp;&nbsp;<S c={pr}>ctx</S>.<S c={fn}>param</S>(<S c={st}>&apos;slow&apos;</S>, {'{'}</div>
          <div>&nbsp;&nbsp;&nbsp;&nbsp;<S c={pr}>type</S>: <S c={st}>&apos;int&apos;</S>, <S c={pr}>default</S>: <S c={nm}>21</S>,</div>
          <div>&nbsp;&nbsp;&nbsp;&nbsp;<S c={pr}>min</S>: <S c={nm}>10</S>, <S c={pr}>max</S>: <S c={nm}>200</S>,</div>
          <div>&nbsp;&nbsp;&nbsp;&nbsp;<S c={pr}>optimize</S>: <S c={kw}>true</S>,</div>
          <div>&nbsp;&nbsp;{'}'});</div>
          <div>{'}'}</div>
          <div className="mt-1"><S c={cm}>{'// Entry logic'}</S></div>
          <div><S c={kw}>if</S> (<S c={fn}>crossUp</S>(<S c={pr}>fast</S>, <S c={pr}>slow</S>))</div>
          <div>&nbsp;&nbsp;<S c={pr}>ctx</S>.<S c={fn}>long</S>()</div>
        </div>

        {/* Parameter summary strip */}
        <div className="px-3 py-2 border-t border-white/[0.06]" style={{ background: '#181825' }}>
          <p className="text-[9px] text-gray-500 uppercase tracking-wider mb-1.5">Active Parameters</p>
          <div className="flex flex-wrap gap-1.5">
            <span className="text-[10px] bg-white/[0.06] text-gray-300 px-2 py-0.5 rounded">fast: <span className="text-white font-medium">9</span> <span className="text-gray-600">[5&ndash;50]</span></span>
            <span className="text-[10px] bg-white/[0.06] text-gray-300 px-2 py-0.5 rounded">slow: <span className="text-white font-medium">21</span> <span className="text-gray-600">[10&ndash;200]</span></span>
          </div>
        </div>
      </div>

      {/* Middle: Validation Metrics */}
      <div className="flex-1 min-w-0 border-b md:border-b-0 md:border-r border-white/[0.06]">
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-white/[0.06]" style={{ background: '#181825' }}>
          <span className="text-[10px] text-cyan-400/80">Validation Results</span>
          <span className="text-[9px] text-gray-600 uppercase tracking-wider">Pipeline</span>
        </div>

        {/* Core metrics */}
        <div className="grid grid-cols-2 gap-px" style={{ background: 'rgba(255,255,255,0.03)' }}>
          {[
            { label: 'Sharpe Ratio', value: '1.92', good: true },
            { label: 'Max Drawdown', value: '-12.4%', good: false },
            { label: 'Win Rate', value: '43.2%', good: null },
            { label: 'Total Return', value: '+34.7%', good: true },
            { label: 'Payoff Ratio', value: '2.14', good: true },
            { label: 'Expectancy', value: '+$24', good: true },
          ].map((m) => (
            <div key={m.label} className="px-3 py-3" style={{ background: '#1e1e2e' }}>
              <p className="text-[8px] text-gray-500 uppercase tracking-wider mb-1">{m.label}</p>
              <p className={`text-[16px] font-bold tracking-tight ${m.good === true ? 'text-emerald-400' : m.good === false ? 'text-red-400' : 'text-white'}`}>{m.value}</p>
            </div>
          ))}
        </div>

        {/* Validation stages */}
        <div className="px-3 py-2.5 border-t border-white/[0.06]" style={{ background: '#181825' }}>
          <p className="text-[9px] text-gray-500 uppercase tracking-wider mb-2">Pipeline Status</p>
          <div className="space-y-1.5">
            {[
              { stage: 'Baseline (142 trades)', pass: true },
              { stage: 'Multi-Asset (6 pairs)', pass: true },
              { stage: 'Walk-Forward (8 windows)', pass: true },
              { stage: 'Forward Validation', pass: true },
            ].map((s) => (
              <div key={s.stage} className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400/70 shrink-0" />
                <span className="text-[10px] text-gray-400">{s.stage}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right: Stability / Optimization */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-white/[0.06]" style={{ background: '#181825' }}>
          <span className="text-[10px] text-[#9d4edd]/80">Stability Analysis</span>
          <span className="text-[9px] text-gray-600 uppercase tracking-wider">Optimization</span>
        </div>

        {/* Cluster heatmap mock */}
        <div className="px-3 py-3 border-b border-white/[0.06]" style={{ background: '#1e1e2e' }}>
          <p className="text-[9px] text-gray-500 uppercase tracking-wider mb-2">Parameter Cluster Map</p>
          <div className="grid grid-cols-10 gap-[2px]">
            {Array.from({ length: 60 }, (_, i) => {
              const scores = [0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.85, 0.9, 0.75, 0.5, 0.4, 0.5, 0.65, 0.75, 0.82, 0.88, 0.92, 0.87, 0.7, 0.45, 0.35, 0.55, 0.7, 0.8, 0.86, 0.91, 0.93, 0.88, 0.72, 0.5, 0.3, 0.5, 0.65, 0.78, 0.84, 0.9, 0.91, 0.85, 0.68, 0.42, 0.25, 0.4, 0.55, 0.7, 0.78, 0.83, 0.85, 0.78, 0.6, 0.35, 0.2, 0.3, 0.45, 0.58, 0.65, 0.72, 0.7, 0.62, 0.48, 0.28];
              const s = scores[i] ?? 0.5;
              const bg = s > 0.85 ? 'rgba(34,197,94,0.7)' : s > 0.7 ? 'rgba(34,197,94,0.35)' : s > 0.5 ? 'rgba(255,255,255,0.08)' : 'rgba(239,68,68,0.2)';
              return <div key={i} className="h-2.5 rounded-[1px]" style={{ background: bg }} />;
            })}
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-[8px] text-gray-600">fast: 5&ndash;50</span>
            <div className="flex items-center gap-2 text-[8px] text-gray-600">
              <span className="flex items-center gap-1"><span className="w-2 h-1.5 rounded-[1px]" style={{ background: 'rgba(239,68,68,0.3)' }} /> Unstable</span>
              <span className="flex items-center gap-1"><span className="w-2 h-1.5 rounded-[1px]" style={{ background: 'rgba(34,197,94,0.7)' }} /> Stable</span>
            </div>
            <span className="text-[8px] text-gray-600">slow: 10&ndash;200</span>
          </div>
        </div>

        {/* WFE + Stability metrics */}
        <div className="grid grid-cols-3 gap-px" style={{ background: 'rgba(255,255,255,0.03)' }}>
          {[
            { label: 'WFE', value: '0.74' },
            { label: 'Cluster Score', value: '82' },
            { label: 'Stable Regions', value: '3' },
          ].map((m) => (
            <div key={m.label} className="px-3 py-3 text-center" style={{ background: '#1e1e2e' }}>
              <p className="text-[8px] text-gray-500 uppercase tracking-wider mb-1">{m.label}</p>
              <p className="text-[16px] font-bold text-white tracking-tight">{m.value}</p>
            </div>
          ))}
        </div>

        {/* Parameter stability strips */}
        <div className="px-3 py-2.5 border-t border-white/[0.06]" style={{ background: '#181825' }}>
          <p className="text-[9px] text-gray-500 uppercase tracking-wider mb-2">Parameter Stability</p>
          <div className="space-y-2">
            {[
              { param: 'fast', selected: 9, range: [7, 15], full: [5, 50] },
              { param: 'slow', selected: 21, range: [18, 35], full: [10, 200] },
            ].map((p) => (
              <div key={p.param}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] text-gray-400">{p.param}</span>
                  <span className="text-[10px] text-emerald-400/80">{p.selected} <span className="text-gray-600">({p.range[0]}&ndash;{p.range[1]})</span></span>
                </div>
                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                  <div
                    className="h-full rounded-full"
                    style={{
                      marginLeft: `${((p.range[0] - p.full[0]) / (p.full[1] - p.full[0])) * 100}%`,
                      width: `${((p.range[1] - p.range[0]) / (p.full[1] - p.full[0])) * 100}%`,
                      background: 'linear-gradient(90deg, rgba(34,197,94,0.4), rgba(34,197,94,0.8), rgba(34,197,94,0.4))',
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default CompositeSystemMockup;
